import { deepMerge, Text } from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import { MRT_ColumnDef, MRT_TableOptions } from 'mantine-react-table';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FieldAnswer } from '../../lib/types/validation';

import { formatDate } from '@data-access';
import { PfTable } from '@ui';

interface ValidationFormFieldAnswersTableProps {
  data: FieldAnswer[];
  onSelectField?: (fieldId: number) => void;
  selectedFieldId?: number | null;
}

export const ValidationFormFieldAnswersTable: FC<
  ValidationFormFieldAnswersTableProps
> = ({ data, onSelectField, selectedFieldId }) => {
  const { t } = useTranslation();

  const columns = useMemo<MRT_ColumnDef<FieldAnswer>[]>(
    () => [
      {
        accessorFn: (row) => row.field.properties.name,
        id: 'field.name',
        header: t('common.properties.field_name'),
        Cell: ({ cell, row }) => {
          const name = cell.getValue<string>();
          return (
            <Text
              span
              style={{
                cursor: onSelectField ? 'pointer' : 'default',
                fontWeight: selectedFieldId === row.original.id ? 'bold' : 'normal',
              }}
              truncate="end"
              onClick={() => onSelectField?.(row.original.id)}
            >
              {name}
            </Text>
          );
        },
      },
      {
        accessorFn: (row) => row.field.properties.area,
        id: 'field.area',
        header: t('common.area'),
        Cell: ({ cell }) => {
          const area = cell.getValue<number>();
          return `${area.toFixed(2)} ha`;
        },
      },
      {
        accessorKey: 'organic',
        header: t('common.properties.organic'),
        Cell: ({ cell }) => {
          const organic = cell.getValue<boolean>();
          return organic ? (
            <IconCheck color="green" size={16} />
          ) : (
            <IconX color="red" size={16} />
          );
        },
      },
      {
        accessorKey: 'last_organic',
        header: t('common.properties.last_organic'),
        Cell: ({ cell }) => {
          const date = cell.getValue<string | null>();
          return date
            ? formatDate({
                date,
                format: 'display',
              })
            : '-';
        },
      },
      {
        accessorKey: 'is_validated',
        header: t('common.properties.is_validated'),
        Cell: ({ cell }) => {
          const validated = cell.getValue<boolean>();
          return validated ? (
            <IconCheck color="green" size={16} />
          ) : (
            <IconX color="red" size={16} />
          );
        },
      },
    ],
    [t, onSelectField, selectedFieldId],
  );

  const tableOptions: MRT_TableOptions<FieldAnswer> = {
    data,
    columns,
    enablePagination: false,
    enableColumnFilters: false,
    enableSorting: true,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableTopToolbar: false,
    mantinePaperProps: {
      h: '100%',
      withBorder: false,
      shadow: 'xs',
    },
    mantineTableProps: {
      striped: true,
      withRowBorders: false,
    },
    getRowId: (row) => row.id.toString(),
  };

  return <PfTable<FieldAnswer> options={tableOptions} />;
};

export default ValidationFormFieldAnswersTable;
